// Floating value readouts for the Scanner (MH Ore Tracker): when an ore is scanned, its worth pops up as
// magenta text above the belt, drifts upward and fades. Canvas-textured THREE.Sprites (auto-billboarded to the
// camera), pooled + reused — ore-through-a-scanner is transient, so a small ring buffer covers it.

import * as THREE from "three";

const CAP = 32;            // max concurrent labels
const RISE = 40;           // studs the label drifts up over its life
const LIFE = 1.1;          // seconds each label lives
const CW = 160, CH = 72;   // label canvas px

export class ScanLabels {
  private group = new THREE.Group();
  private sprites: THREE.Sprite[] = [];
  private tex: THREE.CanvasTexture[] = [];
  private ctx: CanvasRenderingContext2D[] = [];
  private life = new Float32Array(CAP);
  private baseY = new Float32Array(CAP);
  private next = 0;

  constructor(scene: THREE.Scene) {
    this.group.name = "scanlabels";
    scene.add(this.group);
    for (let i = 0; i < CAP; i++) {
      const c = document.createElement("canvas"); c.width = CW; c.height = CH;
      const ctx = c.getContext("2d")!;
      const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace;
      const m = new THREE.SpriteMaterial({ map: t, transparent: true, depthTest: false, depthWrite: false });
      const s = new THREE.Sprite(m);
      s.scale.set(78, 35, 1); s.visible = false; s.renderOrder = 997;
      this.group.add(s);
      this.sprites.push(s); this.tex.push(t); this.ctx.push(ctx);
    }
  }

  setVisible(v: boolean): void { this.group.visible = v; }

  /** Pop a value label at world (x,y,z). Reuses the oldest slot when the pool is full. */
  spawn(x: number, y: number, z: number, value: number): void {
    const i = this.next; this.next = (this.next + 1) % CAP;
    const ctx = this.ctx[i];
    ctx.clearRect(0, 0, CW, CH);
    ctx.font = "bold 38px 'Space Mono', ui-monospace, monospace";
    ctx.textAlign = "center"; ctx.textBaseline = "middle";
    const txt = "◆ " + formatVal(value);
    ctx.lineWidth = 6; ctx.strokeStyle = "rgba(6,2,8,0.92)"; ctx.strokeText(txt, CW / 2, CH / 2); // dark outline for legibility
    ctx.fillStyle = "#e79bff"; ctx.fillText(txt, CW / 2, CH / 2);                                   // magenta, matches the scanner
    this.tex[i].needsUpdate = true;
    const s = this.sprites[i];
    s.position.set(x, y, z); s.visible = true;
    (s.material as THREE.SpriteMaterial).opacity = 1;
    this.baseY[i] = y; this.life[i] = LIFE;
  }

  /** Per-frame: drift active labels up + fade them out. */
  update(dt: number): void {
    for (let i = 0; i < CAP; i++) {
      if (this.life[i] <= 0) continue;
      this.life[i] -= dt;
      const f = Math.max(0, this.life[i] / LIFE); // 1 → 0
      const s = this.sprites[i];
      s.position.y = this.baseY[i] + (1 - f) * RISE;
      (s.material as THREE.SpriteMaterial).opacity = f;
      if (this.life[i] <= 0) s.visible = false;
    }
  }
}

/** Compact money-style formatting so the label stays short (1.2 · 340 · 5.1k · 2.3M). */
function formatVal(v: number): string {
  if (v >= 1e6) return (v / 1e6).toFixed(1) + "M";
  if (v >= 1e3) return (v / 1e3).toFixed(1) + "k";
  if (v >= 100) return String(Math.round(v));
  return v.toFixed(1);
}
